import { UrlPrefixEnum } from '@hyphengroup/cms-components/types/cmsEnums';
import {
  CmsPageLinkProps,
  CMS_CUSTOM_VERTICAL_MAPPING,
  getCmsPageLink,
  getHomePagePath,
  getUrlFragmentForVertical,
  VERTICAL_MAPPING_BY_BRAND,
} from '@hyphengroup/cms-components/utils/routing';
import { BrandId } from 'utils/override';
import type { CmsArticle, CmsPage } from '$/(cms)/_modules/cmsService/types';
import { VerticalType } from '$/components/cms/types';

export * from '@hyphengroup/cms-components/utils/routing';

export const getVerticalTypeFromParamVerticalStr = (props: {
  vertical: string;
  brand: BrandId;
}): VerticalType => {
  const { brand, vertical } = props;

  const MAPPING = VERTICAL_MAPPING_BY_BRAND[brand];
  const res = MAPPING[decodeURIComponent(vertical)];

  return res;
};

// this url starts with vertical prefix, but should not go to CMS
//
// `exact: true` is the bare funnel address only, so `/<vertical>/blog/<slug>` under the same
// vertical stays CMS. `exact: false` is that path and everything under it, on a path boundary.
//
// `brands` limits an entry to the brands named. Omitting it keeps the entry global, which is what
// the entries written before this field were, and changing that is a separate decision per entry.
// An exception that exists because of ONE brand's URL space says so: the list is consulted for
// every brand, so an unscoped entry silently keeps the same address out of CMS everywhere, and no
// test fails on the day another brand starts serving it from the CMS.
export const CMS_EXCEPTIONS_URLS: {
  exact: boolean;
  path: string;
  brands?: BrandId[];
}[] = [
  { exact: true, path: 'car-insurance' },
  { exact: true, path: 'car-insurance/application-success' },
  { exact: true, path: 'car-insurance/list' },
  { exact: true, path: 'car-insurance/results' },
  { exact: false, path: 'travel-insurance/checkout' },
  // SingSaver's blog files insurance content under `home-insurance` and `maid-insurance`, so both
  // slugs are SingSaver blog verticals AND names in its funnel URL space
  // (constants/config.ts: verticals.HOME_INSURANCE, verticals.DOMESTIC_HELPER_INSURANCE).
  //
  // Who serves what, measured on www.singsaver.com.sg:
  //   /home-insurance, /maid-insurance      200 from HubSpot — CloudFront sends both to that origin,
  //                                         and this app has no route for either bare path
  //   /home-insurance/checkout/<carrier>    this app's own route,
  //                                         (application)/home-insurance/checkout/[carrierCode]
  //                                         (+ /success); 404 from HubSpot while the CDN holds it
  //
  // So the checkout entry is the load-bearing one: it is a route this app owns, and without the
  // exception the CMS catch-all claims it the moment SingSaver routes the blog vertical. The two
  // bare paths are held by CloudFront rather than by this list; the entries keep them out of CMS
  // for whichever side ends up serving them, which is why they are scoped to SingSaver and not to
  // MoneyHero, where `/home-insurance` is a page of its own.
  { exact: true, path: 'home-insurance', brands: ['singsaver'] },
  { exact: false, path: 'home-insurance/checkout', brands: ['singsaver'] },
  { exact: true, path: 'maid-insurance', brands: ['singsaver'] },
];

export const CMS_PREFIX = '-cms';
export const CMS_DRAFT_PREFIX = 'draft';

export function combineSlugs(slugs: string[]): string {
  return slugs.join('/');
}

export type PageParams = {
  vertical: string;
  slugs: string[];
};

export const parseVerticalParam = (props: {
  vertical: string;
  brand: BrandId;
}): VerticalType | undefined => {
  const { brand, vertical } = props;

  const MAPPING = VERTICAL_MAPPING_BY_BRAND[brand];
  const res = MAPPING[decodeURIComponent(vertical).toLocaleLowerCase()];

  return res;
};

export type CmsPageAddress = {
  slug: string;
  /** Undefined for custom-vertical prefixes (`promo`, …) — matches the page route. */
  section?: VerticalType;
  urlPrefix: UrlPrefixEnum;
};

/**
 * Resolve a stored page path (a product's `url_alias`, CMS-1924) into the
 * `getPages` params the same way the `[vertical]/[[...slugs]]` page route does:
 * first segment is the vertical (or a custom-vertical prefix), the rest is the
 * slug.
 *
 * Unlike the route — whose params arrive already normalised by Next.js — the
 * input here is a raw CMS field, and what that field actually stores is
 * whatever works as a link. Accepted forms, per market:
 *
 *   /credit-card/products/x                          (SingSaver)
 *   /en/credit-card/products/x                       (MoneyHero — locale prefix)
 *   https://moneyhero.com.hk/en/credit-card/products/x
 *
 * The locale prefix is stripped only when the brand actually uses prefixes
 * (≥2 locales — the same rule `langPrefix` is built with); the locale itself is
 * NOT derived from the path — it arrives separately as `lang`. Returns
 * undefined for anything unresolvable (malformed URL/escape, unknown vertical,
 * missing slug).
 */
export const resolveCmsPageAddress = (props: {
  path: string;
  brand: BrandId;
  locales?: Record<string, string>;
}): CmsPageAddress | undefined => {
  const { brand, locales } = props;

  let path = props.path.trim();
  if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return undefined;
    }
  }
  // CMS slugs are stored unencoded — a percent-encoded alias would never
  // match. The value reaches us from the browser, so a malformed escape must
  // not throw.
  try {
    path = decodeURIComponent(path);
  } catch {
    return undefined;
  }

  const segments = path.split('/').filter(Boolean);
  const brandUsesLocalePrefix = Object.keys(locales ?? {}).length >= 2;
  if (brandUsesLocalePrefix && segments[0] && locales![segments[0]]) {
    segments.shift();
  }
  if (segments.length < 2) return undefined;

  const [verticalParam, ...slugs] = segments;
  const section = parseVerticalParam({ brand, vertical: verticalParam });
  const customUrlPrefix = CMS_CUSTOM_VERTICAL_MAPPING[verticalParam];
  if (!section && !customUrlPrefix) return undefined;

  return {
    slug: combineSlugs(slugs),
    section,
    urlPrefix: customUrlPrefix ?? UrlPrefixEnum.UsePrimaryVertical,
  };
};

/** Absolute URL of a site path: the public origin is always `https://www.<host>`. */
export const toCmsAbsUrl = (host: string, path: string): string =>
  `https://www.${host}${path}`;

/** The flags that move a CMS page off its slug address to a root. */
export type CmsPageAddressFlags = {
  isHomepage?: boolean;
  isVerticalLanding?: boolean;
  isBlogLanding?: boolean;
  isBlogVerticalLanding?: boolean;
};

/** A page's or article's root flags, read the one way every address builder needs them. */
export const getCmsPageAddressFlags = (
  page: CmsPage | CmsArticle,
): CmsPageAddressFlags =>
  page.__typename === 'Page'
    ? {
        isHomepage: page.is_homepage,
        isVerticalLanding: page.is_vertical_landing,
        isBlogLanding: page.blogLanding,
      }
    : { isBlogVerticalLanding: page.blogVerticalLanding };

/**
 * Public path of a CMS page: `isHomepage` at the site root, `isBlogLanding` at the blog root, a section
 * landing (`isVerticalLanding` page, `isBlogVerticalLanding` article) at its section root, any other at
 * its slug. One rule for every address of a page; see docs/page-addresses.md (CMS-2098).
 */
export const getCmsPagePublicPath = (
  props: CmsPageLinkProps & CmsPageAddressFlags,
): string => {
  const { brand, langPrefix, pageType, vertical } = props;
  if (props.isHomepage) return getHomePagePath(langPrefix);
  // the blog home is a page, served where articles live
  if (props.isBlogLanding) {
    return getCmsPageLink({ pageType: 'article', langPrefix, brand });
  }

  const urlPrefix = UrlPrefixEnum.UsePrimaryVertical;
  // a section the brand has no URL for has no root, so the landing stays at its slug
  if (
    (props.isVerticalLanding || props.isBlogVerticalLanding) &&
    getUrlFragmentForVertical({ brand, urlPrefix, vertical })
  ) {
    return getCmsPageLink({ pageType, vertical, urlPrefix, langPrefix, brand });
  }
  return getCmsPageLink({
    pageType,
    vertical,
    slug: props.slug,
    urlPrefix: props.urlPrefix,
    langPrefix,
    brand,
  });
};

/**
 * An authored canonical as an absolute URL, read against the page URL the way a browser reads it;
 * undefined when it cannot be read. Shared by the sitemap and the canonical (CMS-2098).
 */
export const resolveCanonicalUrl = (
  canonicalUrl: string,
  pageUrl: string,
): string | undefined => {
  try {
    return new URL(canonicalUrl, pageUrl).href;
  } catch {
    return undefined;
  }
};

/** Absolute URL of a CMS page, by `getCmsPagePublicPath`: the one door for a page's own address. */
export const getCmsAbsPageLink = (
  props: CmsPageLinkProps & CmsPageAddressFlags & { host: string },
): string => {
  return toCmsAbsUrl(props.host, getCmsPagePublicPath(props));
};
